import { computed, onBeforeUnmount, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const COUNT_PREFIX = /^\(\d+\+?\)\s*/;
const MAX_DISPLAY = 99;

export const formatTabTitle = (baseTitle, count) => {
  const title = (baseTitle || '').replace(COUNT_PREFIX, '');
  if (!count || count <= 0) return title;
  const label = count > MAX_DISPLAY ? `${MAX_DISPLAY}+` : String(count);
  return `(${label}) ${title}`;
};

/**
 * Mirrors the number of unread conversations visible to the agent in the browser tab title,
 * e.g. "(5) Wasapi". Uses Chatwoot's conversation unread counters (realtime via ActionCable)
 * when the account has `conversation_unread_counts`, falling back to unread notifications.
 */
export function useUnreadTabTitle() {
  const { accountId, isCloudFeatureEnabled } = useAccount();
  const conversationUnread = useMapGetter(
    'conversationUnreadCounts/getAllUnreadCount'
  );
  const notificationUnread = useMapGetter('notifications/getUnreadCount');

  const baseTitle = document.title.replace(COUNT_PREFIX, '');

  const hasConversationCounts = computed(() => {
    if (!accountId.value) return false;
    try {
      return isCloudFeatureEnabled(FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS);
    } catch {
      return false;
    }
  });

  const unreadCount = computed(() =>
    hasConversationCounts.value
      ? Number(conversationUnread.value) || 0
      : Number(notificationUnread.value) || 0
  );

  watch(
    unreadCount,
    count => {
      document.title = formatTabTitle(baseTitle, count);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    document.title = baseTitle;
  });

  return { unreadCount };
}
